import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid-pro';
import {
  Box, Select, Chip, Badge, TextField, Button, Menu, MenuItem, Checkbox, Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DoneIcon from '@mui/icons-material/Done';
import Table from '../components/Table';

const CHECK_MARK = '✔';

// eslint-disable-next-line react/no-unstable-nested-components
function IdentitiesToolbar() {
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarExport
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ fileName: 'client-identities', utf8WithBom: true }}
      />
    </GridToolbarContainer>
  );
}

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function IdentitiesFooter({ shownCount, totalCount }) {
  return (
    <Box sx={{ padding: '8px 14px', fontSize: '0.8rem', color: 'text.secondary' }}>
      {`showing ${shownCount} of ${totalCount} clients`}
    </Box>
  );
}

IdentitiesFooter.propTypes = {
  shownCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
};

function ClientIdentities({
  clients, handleViewChange, isLoading, view,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIdentities, setSelectedIdentities] = useState(
    () => searchParams.get('identity')?.split(',').filter(Boolean) || [],
  );
  const [searchValue, setSearchValue] = useState(() => searchParams.get('q') || '');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: 'client', sort: 'asc' }]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (selectedIdentities.length > 0) {
        next.set('identity', selectedIdentities.join(','));
      } else {
        next.delete('identity');
      }
      if (searchValue) {
        next.set('q', searchValue);
      } else {
        next.delete('q');
      }
      return next;
    }, { replace: true });
  }, [selectedIdentities, searchValue, setSearchParams]);
  const identityTags = useMemo(() => {
    const tagSet = new Set();
    clients.forEach((client) => {
      (client.identity || []).forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [clients]);

  const filteredClients = useMemo(() => {
    const searchRegex = searchValue ? new RegExp(escapeRegExp(searchValue), 'i') : null;
    return clients.filter((client) => {
      const matchesSearch = !searchRegex
        || searchRegex.test(client.fullname || '')
        || searchRegex.test(client.code || '');
      const clientIdentities = client.identity || [];
      const matchesIdentity = selectedIdentities.length === 0
        || selectedIdentities.every((tag) => clientIdentities.includes(tag));
      return matchesSearch && matchesIdentity;
    });
  }, [clients, searchValue, selectedIdentities]);

  const columns = useMemo(() => [
    {
      field: 'client',
      headerName: 'Client',
      headerClassName: 'client-column-header',
      minWidth: 200,
      flex: 1,
      valueGetter: (value, row) => row.fullname,
    },
    ...identityTags.map((tag) => ({
      field: `tag_${tag}`,
      headerName: tag,
      headerClassName: 'vertical-table-header',
      sortable: false,
      filterable: false,
      width: 50,
      valueGetter: (value, row) => ((row.identity || []).includes(tag) ? CHECK_MARK : ''),
      renderCell: (params) => (
        params.value
          ? <DoneIcon fontSize="small" color="success" />
          : null
      ),
    })),
  ], [identityTags]);

  const toggleIdentity = (tag) => {
    setSelectedIdentities((prev) => (
      prev.includes(tag) ? prev.filter((selected) => selected !== tag) : [...prev, tag]
    ));
  };

  const pageState = useMemo(() => ({ data: filteredClients, isLoading }), [
    filteredClients, isLoading,
  ]);

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2, flexWrap: 'wrap',
      }}
      >
        <h2>Client Identities</h2>
        <Chip
          label={`${identityTags.length} TAGS · ${clients.length} CLIENTS`}
          size="small"
          color="success"
          variant="outlined"
        />
        <Box sx={{ flex: 1 }} />
        <Select
          value={view}
          onChange={handleViewChange}
          size="small"
        >
          <MenuItem value="clientlist">Client List</MenuItem>
          <MenuItem value="clientidentities">Client Identities</MenuItem>
          <MenuItem value="deliverysettings">Delivery Settings</MenuItem>
        </Select>
        <Badge badgeContent={selectedIdentities.length} color="primary">
          <Button
            variant="outlined"
            endIcon={<ArrowDropDownIcon />}
            onClick={(event) => setFilterAnchorEl(event.currentTarget)}
          >
            Filter by identity
          </Button>
        </Badge>
        <TextField
          label="Search client name or id"
          size="small"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
        />
      </Box>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <p style={{ marginLeft: '10px' }}>{`${selectedIdentities.length} selected`}</p>
        <Button size="small" onClick={() => setSelectedIdentities([])}>Clear</Button>
        <Divider />
        <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {identityTags.map((tag) => (
            <MenuItem key={tag} onClick={() => toggleIdentity(tag)} dense>
              <Checkbox
                checked={selectedIdentities.includes(tag)}
                size="small"
                sx={{ padding: 0, marginRight: 1 }}
              />
              {tag}
            </MenuItem>
          ))}
        </Box>
      </Menu>
      <Box sx={{ height: 800, maxWidth: '100%', overflowX: 'auto' }}>
        <Table
          pageState={pageState}
          columns={columns}
          sortModel={sortModel}
          setSortModel={setSortModel}
          columnVisibilityModel={{}}
          customSlots={{ toolbar: IdentitiesToolbar, footer: IdentitiesFooter }}
          slotProps={{
            footer: { shownCount: filteredClients.length, totalCount: clients.length },
          }}
          customInitialState={{ pinnedColumns: { left: ['client'] } }}
          columnHeaderHeight={200}
          tablePrefix="client-identities"
          disableColumnMenu
        />
      </Box>
    </Box>
  );
}

ClientIdentities.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    fullname: PropTypes.string.isRequired,
  })).isRequired,
  handleViewChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  view: PropTypes.string,
};

export default ClientIdentities;
